import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    batchUpdateRecommendationCaregiverSelections,
    type RecommendationUnitType,
} from '@apps/web/lib/recommendation_selection_api';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

// The selection and rebuild events drive the refresh; this poll only
// backstops a dropped event, so it stays close to the old cadence.
const SNAPSHOT_REFRESH_INTERVAL_MS = 30_000;

export function useOutreachCandidateSnapshot(
    scope: HarnessScope,
    outreachId: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/snapshot/by-outreach',
        method: 'get',
        pathParams: { organization_id: scope.organizationId },
        queryParams: { outreach_id: outreachId },
        mode: scope.mode,
        reason: 'v3-chat-recommendations-card',
        refreshInterval: SNAPSHOT_REFRESH_INTERVAL_MS,
        refreshWhenHidden: false,
        select: (response) => response,
    });
}

/**
 * Tick or untick caregivers on one decision unit.
 *
 * Delegates to the helper the outreach page uses, so batching, idempotence,
 * and the per-item result contract have one implementation. Unticking a
 * caregiver the coordinator staged withdraws it; unticking one the agent
 * staged dismisses it, which is durable and blocks later restaging.
 *
 * Returns the caregiver ids the server refused, so the caller can roll its
 * optimistic tick back instead of showing a write that never landed.
 */
export async function applyCandidateSelections(
    scope: HarnessScope,
    {
        outreachId,
        unitType,
        unitId,
        staged,
        stagedByUser,
    }: Readonly<{
        readonly outreachId: string;
        readonly unitType: RecommendationUnitType;
        readonly unitId: string;
        readonly staged: ReadonlyMap<string, boolean>;
        readonly stagedByUser: ReadonlySet<string>;
    }>,
): Promise<ReadonlySet<string>> {
    const response = await batchUpdateRecommendationCaregiverSelections({
        organizationId: scope.organizationId,
        outreachId,
        unitType,
        unitId,
        mode: scope.mode,
        reason: 'v3-chat-recommendations-selection',
        items: Array.from(staged, ([caregiverId, isStaged]) => ({
            caregiver_id: caregiverId,
            action: isStaged
                ? ('stage' as const)
                : stagedByUser.has(caregiverId)
                  ? ('withdraw' as const)
                  : ('dismiss' as const),
        })),
    });
    return new Set(
        response.items
            .filter((item) => item.error_code !== null)
            .map((item) => item.caregiver_id),
    );
}
